import { useReactiveVar } from "@apollo/client";
import compact from "lodash/compact";

import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import {
  LeftSidePanelState,
  currentOrganizationVar,
  currentUserVar,
  leftSidePanelVar,
} from "@cache/cache";
import Tooltip from "@components/tooltip/tooltip";
import { getTopicflowIcon } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";

type SidebarTab = {
  id: string;
  name: string;
  icon: ReturnType<typeof getTopicflowIcon>;
  panel: LeftSidePanelState;
  isActive: (panel: LeftSidePanelState) => boolean;
};

const SidebarTabs = () => {
  const leftSidePanel = useReactiveVar(leftSidePanelVar);
  const currentUser = currentUserVar();
  const currentOrganization = currentOrganizationVar();
  const { uiPreferenceCache, saveUiPreference } = useUiPreferenceCache();

  const hasReports = currentUser.directReports.edges.length > 0;

  const tabs: SidebarTab[] = compact([
    {
      id: "main",
      name: "Main",
      icon: getTopicflowIcon("viewGrid"),
      panel: false,
      isActive: (panel) => panel === false,
    },
    currentOrganization.featureFlags.meetings && {
      id: "meetings",
      name: "Meetings",
      icon: getTopicflowIcon("meeting"),
      panel: "meetings",
      isActive: (panel) => panel === "meetings" || typeof panel === "object",
    },
    hasReports && {
      id: "reports",
      name: "People",
      icon: getTopicflowIcon("users"),
      panel: "reports",
      isActive: (panel) => panel === "reports",
    },
    currentOrganization.featureFlags.askAi && {
      id: "chats",
      name: "Chats",
      icon: getTopicflowIcon("messagesDotsSquare"),
      panel: "chats",
      isActive: (panel) => panel === "chats",
    },
    {
      id: "settings",
      name: "Settings",
      icon: getTopicflowIcon("settings02"),
      panel: "settings",
      isActive: (panel) => panel === "settings",
    },
  ]);

  const handleClickTab = (tab: SidebarTab) => {
    if (!uiPreferenceCache.leftNavigationSidebarExpanded) {
      saveUiPreference({ leftNavigationSidebarExpanded: true });
    }
    leftSidePanelVar(tab.panel);
  };

  return (
    <div className="flex items-center gap-1 px-3 py-2">
      {tabs.map((tab) => {
        const isActive = tab.isActive(leftSidePanel);
        return (
          <Tooltip
            key={tab.id}
            placement="bottom"
            text={isActive ? "" : tab.name}
          >
            <button
              aria-label={tab.name}
              aria-current={isActive ? "page" : undefined}
              onClick={() => handleClickTab(tab)}
              className={classNames(
                "flex items-center rounded-full transition-colors shrink-0",
                isActive
                  ? "gap-1.5 pl-2.5 pr-3 py-1.5 bg-nav-item-active text-gray-800 font-medium"
                  : "p-1.5 text-gray-500 hover:bg-nav-item-active hover:text-gray-700",
              )}
            >
              <tab.icon className="h-4 w-4 shrink-0" />
              {isActive && (
                <span className="text-sm whitespace-nowrap">{tab.name}</span>
              )}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
};

export default SidebarTabs;
